import React, { useContext, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';
import { Plus, Edit, Trash2, Eye, Power } from 'lucide-react';
import TableAdmin from '../../components/admin/TableAdmin';
import ModalAdmin from '../../components/admin/ModalAdmin';
import SearchBar from '../../components/admin/SearchBar';
import Pagination from '../../components/admin/Pagination';
import Watermark from '../../components/common/Watermark';

const CreatorSpace = () => {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const isDark = theme === 'dark';

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [currentPageProducts, setCurrentPageProducts] = useState(1);
  const [totalPagesProducts, setTotalPagesProducts] = useState(1);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productFormMode, setProductFormMode] = useState('create');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productSaleType, setProductSaleType] = useState('libre');
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showArchives, setShowArchives] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const fileInputRef = useRef(null);
  const stockInputRef = useRef(null);

  // Résoudre l'URL publique d'une image produit (priorité watermark, via route protégée)
  const resolveProductImageUrl = (p) => {
    if (!p) return null;
    // Priorité: path_watermarked > path_image > autres
    const path = p.path_watermarked || p.path_image || p.image || p.image_url || p.picture_product || (p.images && p.images[0] && (p.images[0].path_watermarked || p.images[0].path_image));
    if (!path) return null;
    if (String(path).startsWith('http')) return path;
    
    // Les images sont servies via la route protégée /api/images/{path}
    let cleanPath = String(path).replace(/^\/+/, ''); // retirer les / au début
    if (cleanPath.startsWith('storage/')) {
      cleanPath = cleanPath.substring(8); // retirer 'storage/'
    }
    
    // Utiliser la route protégée de l'API
    const finalUrl = `https://api.krayneagency.fr/api/images/${cleanPath}`;
    return finalUrl;
  };

  const itemsPerPage = 10;

  // Charger les catégories
  useEffect(() => {
    fetchCategories();
  }, []);

  // Charger les utilisateurs pour ventes uniques
  useEffect(() => {
    fetchAllUsers();
  }, []);

  // Charger les produits du créateur
  useEffect(() => {
    fetchCreatorProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      let cats = response.data?.data || response.data || [];
      // Si c'est un objet, convertir en array
      if (typeof cats === 'object' && !Array.isArray(cats)) {
        cats = Object.values(cats);
      }
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error('Erreur catégories:', error);
      setCategories([]);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/users');
      const allUsers = response.data?.data || response.data || [];
      // Filtrer l'utilisateur actuel
      const currentId = user?.id_user || user?.id;
      const filteredUsers = allUsers.filter(u => (u.id_user || u.id) !== currentId);
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Erreur utilisateurs:', error);
    }
  };

  const fetchCreatorProducts = async () => {
    try {
      console.log('Fetching products for creator (self)');
      // Utiliser l'endpoint qui renvoie la liste des produits du créateur connecté
      const response = await api.get(`/creator/products`);
      console.log('Creator products response:', response.data);
      const raw = response.data?.data || response.data || [];
      const API_URL = 'https://api.krayneagency.fr';
      const getImageUrl = (img) => {
        if (!img) return null;
        // Normaliser le chemin
        let clean = String(img).replace(/^\/+/, ''); // retirer les / au début
        if (clean.startsWith('storage/')) clean = clean.substring(8);
        // Servir via la route protégée API
        return `${API_URL}/api/images/${clean}`;
      };
      const mapped = raw.map((p) => {
        const allowed = (p.allowed_users || p.allowedUsers || []).map((u) => {
          if (typeof u === 'object') return String(u.id_user || u.id);
          return String(u);
        });
        
        // Récupérer l'image depuis images array si disponible
        let imageData = null;
        if (p.images && Array.isArray(p.images) && p.images.length > 0) {
          imageData = p.images[0]; // objet {path_image, path_watermarked, ...}
        }
        
        return {
          id: p.id_product ?? p.id,
          id_product: p.id_product ?? p.id,
          name: p.name ?? p.name_product ?? '',
          category: p.category?.name ?? p.category ?? p.category_name ?? '',
          price: p.price_ttc ?? p.price ?? p.price_product ?? 0,
          price_ht: p.price ?? p.price_product ?? 0,
          stock: p.available_stock ?? p.stock ?? 0,
          sale_type: p.sale_type ?? '',
          status: p.status ?? '',
          description: p.description ?? p.description_product ?? '',
          image: p.image ?? (imageData ? getImageUrl(imageData.path_watermarked || imageData.path_image) : null),
          images: p.images || [], // Garder la relation images complète
          path_watermarked: imageData?.path_watermarked || null,
          path_image: imageData?.path_image || null,
          created_at: p.created_at ?? '',
          allowed_users: allowed,
        };
      });
      setProducts(mapped);
      setFilteredProducts(mapped);
      setTotalPagesProducts(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
    } catch (error) {
      console.error('Erreur produits:', error);
      toast.error('Impossible de charger vos produits');
    }
  };

  useEffect(() => {
    const query = searchProduct.toLowerCase();
    const filtered = products.filter(p => {
      // Filtrer par status (actif vs inactif/archives)
      const matchesStatus = showArchives 
        ? (p.status === 'inactive' || p.status === 'archived')
        : p.status === 'active';
      
      // Filtrer par recherche
      const matchesQuery = query
        ? (p.name || '').toLowerCase().includes(query) ||
          (p.category || '').toLowerCase().includes(query)
        : true;
      
      return matchesStatus && matchesQuery;
    });

    setFilteredProducts(filtered);
    setTotalPagesProducts(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    setCurrentPageProducts(1);
  }, [searchProduct, products, showArchives]);

  const handleCreateProduct = () => {
    setProductFormMode('create');
    setSelectedProduct({
      name: '',
      category: '',
      price: '',
      stock: 1,
      sale_type: 'libre',
      status: 'inactive', // En attente de validation
      description: '',
    });
    setProductSaleType('libre');
    setSelectedCategory('');
    setAllowedUsers([]);
    setUploadedImage(null);
    setImagePreview(null);
    setProductModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setProductFormMode('edit');
    setSelectedProduct(product);
    setProductSaleType(product.sale_type || 'libre');
    
    // Définir la catégorie sélectionnée
    const categoryName = typeof product.category === 'object' 
      ? (product.category?.name || product.category?.name_category || '')
      : (product.category || '');
    setSelectedCategory(categoryName);
    
    // Charger les utilisateurs autorisés si vente unique
    if (product.sale_type === 'unique' && product.allowed_users) {
      setAllowedUsers(product.allowed_users.map(u => {
        if (typeof u === 'object') return String(u.id_user || u.id);
        return String(u);
      }));
    } else {
      setAllowedUsers([]);
    }
    setUploadedImage(null);
    setImagePreview(null);
    setProductModalOpen(true);
    
    // Initialiser le calcul TVA après ouverture du modal
    setTimeout(() => {
      const priceInput = document.querySelector('input[name="price"]');
      if (priceInput) {
        const priceHT = parseFloat(priceInput.value) || parseFloat(product?.price_ht) || parseFloat(product?.price) || 0;
        const tva = priceHT * 0.20;
        const priceTTC = priceHT + tva;
        
        const priceDiv = document.querySelector('.price-calc-display-creator');
        if (priceDiv && priceHT > 0) {
          priceDiv.innerHTML = `
            <div class="text-xs space-y-1.5">
              <div class="flex justify-between"><span>Prix HT:</span><span class="font-semibold">${priceHT.toFixed(2)}€</span></div>
              <div class="flex justify-between"><span>TVA (20%):</span><span class="text-orange-500">${tva.toFixed(2)}€</span></div>
              <div class="border-t border-gray-300 dark:border-white/10 pt-1.5 flex justify-between"><span class="font-semibold">Prix TTC:</span><span class="font-bold text-rose-500">${priceTTC.toFixed(2)}€</span></div>
            </div>
          `;
        }
      }
    }, 200);
  };

  const handleViewProduct = (product) => {
    setProductFormMode('view');
    setSelectedProduct(product);
    setProductSaleType(product.sale_type || 'libre');
    if (product.sale_type === 'unique' && product.allowed_users) {
      setAllowedUsers(product.allowed_users.map(u => {
        if (typeof u === 'object') return String(u.id_user || u.id);
        return String(u);
      }));
    } else {
      setAllowedUsers([]);
    }
    setUploadedImage(null);
    setImagePreview(null);
    setProductModalOpen(true);
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Voulez-vous vraiment supprimer "${product.name}" ?`)) {
      try {
        await api.delete(`/creator/products/${product.id_product || product.id}`, {
          data: { id_user: user?.id_user || user?.id }
        });
        setProducts(products.filter(p => (p.id_product || p.id) !== (product.id_product || product.id)));
        toast.success('Produit supprimé');
      } catch (error) {
        console.error('Erreur suppression:', error);
        toast.error('Impossible de supprimer le produit');
      }
    }
  };

  const handleToggleProductStatus = async (product) => {
    try {
      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      await api.patch(`/creator/products/${product.id_product || product.id}/status`, {
        status: newStatus,
        id_user: user?.id_user || user?.id
      });
      
      // Mettre à jour le statut localement
      setProducts(products.map(p => 
        (p.id_product || p.id) === (product.id_product || product.id) 
          ? { ...p, status: newStatus }
          : p
      ));
      
      toast.success(`Produit ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
    } catch (error) {
      console.error('Erreur toggle statut:', error);
      toast.error('Impossible de changer le statut du produit');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log('Image sélectionnée:', file.name, 'Taille:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        
        // Options de compression
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        };
        
        // Compresser l'image
        const compressedFile = await imageCompression(file, options);
        console.log('Image compressée:', compressedFile.name, 'Taille:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
        
        // Normaliser en File (le compresseur peut retourner un Blob)
        const finalFile = compressedFile instanceof File
          ? compressedFile
          : new File([compressedFile], file.name, { type: file.type || 'image/jpeg' });

        // Stocker le fichier compressé
        setUploadedImage(finalFile);
        
        // Créer un aperçu
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Erreur lors de la compression:', error);
        toast.error('Erreur lors du traitement de l\'image');
      }
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      // Validation: vérifier qu'une image est fournie pour la création
      if (productFormMode === 'create' && !uploadedImage) {
        toast.error('Veuillez sélectionner une image pour créer un produit');
        return;
      }

      // Validation: vérifier qu'UN utilisateur est sélectionné pour les ventes uniques
      if (productData.sale_type === 'unique' && allowedUsers.length === 0) {
        toast.error('Veuillez sélectionner UN utilisateur pour cette vente unique');
        return;
      }
      if (productData.sale_type === 'unique' && allowedUsers.length > 1) {
        toast.error('Une vente unique ne peut avoir qu\'UN SEUL acheteur');
        return;
      }

      const formData = new FormData();
      formData.append('name', productData.name);
      formData.append('category', productData.category);
      formData.append('price', productData.price);
      formData.append('stock', productData.stock);
      formData.append('sale_type', productData.sale_type);
      formData.append('description', productData.description || '');

      // Ajouter les utilisateurs autorisés pour ventes uniques
      if (productData.sale_type === 'unique' && allowedUsers.length > 0) {
        allowedUsers.forEach((userId) => {
          formData.append('allowed_users[]', userId);
        });
      }

      // Gestion de l'image
      if (uploadedImage) {
        console.log('📸 Image à uploader:', uploadedImage instanceof File ? 'File' : typeof uploadedImage);
        // Si c'est un File directement (depuis l'input)
        if (uploadedImage instanceof File || uploadedImage instanceof Blob) {
          const fileToSend = uploadedImage instanceof File
            ? uploadedImage
            : new File([uploadedImage], 'image.png', { type: uploadedImage.type || 'image/png' });
          formData.append('image', fileToSend);
          console.log('✅ Image ajoutée au FormData');
        } 
        // Si c'est une dataURL (voir si conversion nécessaire)
        else if (typeof uploadedImage === 'string' && uploadedImage.startsWith('data:')) {
          try {
            const response = await fetch(uploadedImage);
            const blob = await response.blob();
            formData.append('image', blob, 'image.png');
            console.log('✅ Image convertie et ajoutée au FormData');
          } catch (err) {
            console.error('Erreur conversion image:', err);
            toast.error('Erreur lors du traitement de l\'image');
            return;
          }
        }
      } else if (productFormMode === 'edit') {
        console.log('ℹ️ Pas de nouvelle image - conservation de l\'image existante');
      }

      if (productFormMode === 'create') {
        const response = await api.post('/creator/products', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Produit créé ! En attente de validation par un admin.');
      } else {
        formData.append('_method', 'PUT');
        const response = await api.post(`/creator/products/${selectedProduct.id_product || selectedProduct.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Produit modifié !');
      }

      setProductModalOpen(false);
      setUploadedImage(null);
      setImagePreview(null);
      fetchCreatorProducts();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.errors || error.message;
      toast.error('Erreur: ' + JSON.stringify(errorMsg));
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'inactive':
        return 'Inactif';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  const displayedProducts = filteredProducts.slice(
    (currentPageProducts - 1) * itemsPerPage,
    currentPageProducts * itemsPerPage
  );

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Mes Produits</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Gérez vos créations et les postez pour approbation
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <SearchBar
              value={searchProduct}
              onChange={setSearchProduct}
              placeholder="Rechercher vos produits..."
            />
            <button
              onClick={() => setShowArchives(!showArchives)}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                showArchives
                  ? isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'
                  : isDark ? 'bg-white/10 text-gray-400 hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={showArchives ? 'Afficher actifs' : 'Afficher archives'}
            >
              {showArchives ? '📦 Archives' : '✓ Actifs'}
            </button>
          </div>
          <button
            onClick={handleCreateProduct}
            className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Nouveau produit
          </button>
        </div>

        {/* Table */}
        {displayedProducts.length === 0 ? (
          <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-white border border-gray-200'}`}>
            <p className="text-sm opacity-70">
              {showArchives 
                ? 'Aucun produit archivé pour le moment' 
                : 'Aucun produit actif pour le moment'}
            </p>
            <button
              onClick={handleCreateProduct}
              className="mt-4 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 text-sm"
            >
              Créer votre premier produit
            </button>
          </div>
        ) : (
          <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Image</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Nom</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Catégorie</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Prix HT</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Stock</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Statut</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedProducts.map((product) => (
                  <tr key={product.id} className={`border-b ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}>
                    <td className="px-6 py-4 text-sm">
                      {resolveProductImageUrl(product) && (
                        <Watermark>
                          <img
                            src={resolveProductImageUrl(product)}
                            alt={product.name}
                            className="max-h-10 rounded"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </Watermark>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{product.name}</td>
                    <td className="px-6 py-4 text-sm">{product.category}</td>
                    <td className="px-6 py-4 text-sm">{product.price_ht}€</td>
                    <td className="px-6 py-4 text-sm">{product.stock === 0 ? 'Illimité' : product.stock}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        product.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {getStatusLabel(product.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewProduct(product)}
                          className={`p-2 rounded transition ${isDark ? 'hover:bg-blue-500/10 text-blue-500' : 'hover:bg-blue-50 text-blue-600'}`}
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditProduct(product)}
                          className={`p-2 rounded transition ${isDark ? 'hover:bg-yellow-500/10 text-yellow-400' : 'hover:bg-yellow-50 text-yellow-600'}`}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleProductStatus(product)}
                          className={`p-2 rounded transition ${product.status === 'active' 
                            ? isDark ? 'hover:bg-green-500/10 text-green-500' : 'hover:bg-green-50 text-green-600'
                            : isDark ? 'hover:bg-orange-500/10 text-orange-500' : 'hover:bg-orange-50 text-orange-600'
                          }`}
                          title={product.status === 'active' ? 'Désactiver' : 'Activer'}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product)}
                          className={`p-2 rounded transition ${isDark ? 'hover:bg-red-500/10 text-red-500' : 'hover:bg-red-50 text-red-600'}`}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPagesProducts > 1 && (
          <Pagination
            currentPage={currentPageProducts}
            totalPages={totalPagesProducts}
            onPageChange={setCurrentPageProducts}
          />
        )}

        {/* Modal */}
        <ModalAdmin
          isOpen={productModalOpen}
          onClose={() => {
            setProductModalOpen(false);
            setUploadedImage(null);
            setImagePreview(null);
          }}
          title={productFormMode === 'create' ? 'Créer un produit' : productFormMode === 'edit' ? 'Modifier le produit' : 'Détails du produit'}
          size="lg"
        >
          {productFormMode !== 'view' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveProduct({
                  name: e.target.elements.name.value,
                  category: selectedCategory,
                  price: parseFloat(e.target.elements.price.value),
                  stock: parseInt(e.target.elements.stock.value),
                  sale_type: e.target.elements.sale_type.value,
                  description: e.target.elements.description.value,
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Nom <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={selectedProduct?.name || ''}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Catégorie <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="">-- Sélectionner --</option>
                    {categories.map(cat => (
                      <option key={cat.id_category || cat.id} value={cat.name || cat.name_category}>
                        {cat.name || cat.name_category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Prix HT (€) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    step="0.01"
                    defaultValue={selectedProduct?.price_ht || ''}
                    required
                    onInput={(e) => {
                      const priceHT = parseFloat(e.target.value) || 0;
                      const tva = priceHT * 0.20;
                      const priceTTC = priceHT + tva;
                      
                      // Mettre à jour la div de calcul
                      const priceDiv = document.querySelector('.price-calc-display-creator');
                      if (priceDiv) {
                        priceDiv.innerHTML = `
                          <div class="text-xs space-y-1.5">
                            <div class="flex justify-between"><span>Prix HT:</span><span class="font-semibold">${priceHT.toFixed(2)}€</span></div>
                            <div class="flex justify-between"><span>TVA (20%):</span><span class="text-orange-500">${tva.toFixed(2)}€</span></div>
                            <div class="border-t border-gray-300 dark:border-white/10 pt-1.5 flex justify-between"><span class="font-semibold">Prix TTC:</span><span class="font-bold text-rose-500">${priceTTC.toFixed(2)}€</span></div>
                          </div>
                        `;
                      }
                    }}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Calcul TVA
                  </label>
                  <div className={`price-calc-display-creator w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                    <div className="text-xs text-center opacity-50">Saisissez un prix HT</div>
                  </div>
                </div>
                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Stock <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="stock"
                    min="0"
                    defaultValue={selectedProduct?.stock ?? '1'}
                    required
                    placeholder="0 = illimité"
                    ref={stockInputRef}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (stockInputRef.current) {
                        stockInputRef.current.value = 0;
                        stockInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
                      }
                    }}
                    className={`absolute top-0 right-0 mt-1 mr-1 text-xs px-2 py-1 rounded ${isDark ? 'bg-rose-500/20 text-rose-200 hover:bg-rose-500/30' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}
                  >
                    Illimité
                  </button>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    0 = Stock illimité
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Type de vente <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="sale_type"
                    value={productSaleType}
                    onChange={(e) => {
                      setProductSaleType(e.target.value);
                      if (e.target.value === 'libre') {
                        setAllowedUsers([]);
                      } else if (e.target.value === 'unique' && allowedUsers.length === 0) {
                        // Pré-sélectionner l'utilisateur actuel si c'est vente unique et personne n'est sélectionné
                        if (user && user.id_user) {
                          setAllowedUsers([String(user.id_user)]);
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="libre">Libre</option>
                    <option value="unique">Pièce unique</option>
                  </select>
                </div>
              </div>

              {/* UI pour ventes uniques - sélection d'utilisateurs */}
              {productSaleType === 'unique' && (
                <div className={`p-5 rounded-lg border-2 ${isDark ? 'border-rose-500/40 bg-gradient-to-br from-rose-500/10 to-rose-500/5' : 'border-rose-300 bg-gradient-to-br from-rose-50 to-rose-25'}`}>
                  {/* Header avec icône */}
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                      Utilisateurs autorisés <span className="text-rose-500">*</span>
                    </h3>
                  </div>

                  {/* Description */}
                  <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-semibold text-rose-500">⚠️ Vente unique :</span> Sélectionnez UN SEUL utilisateur qui pourra voir et acheter cette création exclusive.
                  </p>

                  {/* Bouton désélectionner */}
                  {allowedUsers.length > 0 && (
                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={() => setAllowedUsers([])}
                        className={`text-xs px-3 py-1.5 rounded font-medium transition-all ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                      >
                        ✕ Désélectionner
                      </button>
                    </div>
                  )}

                  {/* Résumé des sélectionnés */}
                  {allowedUsers.length > 0 && (
                    <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-rose-500/10 border border-rose-500/30' : 'bg-rose-50 border border-rose-200'}`}>
                      <p className={`text-sm font-semibold ${isDark ? 'text-rose-200' : 'text-rose-700'}`}>
                        ✓ {allowedUsers.length} utilisateur{allowedUsers.length > 1 ? 's' : ''} sélectionné{allowedUsers.length > 1 ? 's' : ''}
                      </p>
                      <div className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {allowedUsers.map((userId, idx) => {
                          const user = users.find(u => String(u.id_user || u.id) === userId);
                          const label = user ? (user.nickname || `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email) : '';
                          return (
                            <div key={userId} className="text-sm">
                              • {label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Liste des utilisateurs */}
                  <div className="max-h-60 overflow-y-auto border rounded-lg p-3 pr-2" style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {users.map((u) => {
                        const id = u.id_user || u.id;
                        const label = u.nickname || `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email;
                        const checked = allowedUsers.includes(String(id));
                        return (
                          <label key={id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            checked 
                              ? isDark 
                                ? 'bg-rose-500/15 border-rose-500/50' 
                                : 'bg-rose-50 border-rose-300'
                              : isDark
                                ? 'bg-white/2 border-white/10 hover:bg-white/5'
                                : 'bg-white/30 border-gray-200 hover:bg-gray-50'
                          }`}>
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              checked
                                ? isDark
                                  ? 'bg-rose-500/40 border-rose-500'
                                  : 'bg-rose-500 border-rose-500'
                                : isDark
                                  ? 'border-gray-600'
                                  : 'border-gray-300'
                            }`}>
                              {checked && (
                                <svg className={`w-3 h-3 ${isDark ? 'text-rose-200' : 'text-white'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <input
                              type="radio"
                              name="unique_user"
                              checked={checked}
                              onChange={() => {
                                // Pour une vente unique, un seul utilisateur peut être sélectionné
                                setAllowedUsers([String(id)]);
                              }}
                              className="hidden"
                              aria-label={label}
                            />
                            {u.photo && (
                              <img
                                src={u.photo}
                                alt={label}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-transparent"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                                {label}
                              </p>
                              {u.email && (
                                <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {u.email}
                                </p>
                              )}
                            </div>
                            {checked && (
                              <div className="flex-shrink-0">
                                <div className={`text-xs font-semibold px-2 py-1 rounded ${isDark ? 'bg-rose-500/40 text-rose-200' : 'bg-rose-100 text-rose-700'}`}>
                                  ✓
                                </div>
                              </div>
                            )}
                          </label>
                        );
                      })}
                      {users.length === 0 && (
                        <div className={`col-span-2 text-center py-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Aucun utilisateur trouvé
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Avertissement si vide */}
                  {allowedUsers.length === 0 && (
                    <div className={`mt-3 p-3 rounded-lg text-xs ${isDark ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                      ⚠️ Veuillez sélectionner UN utilisateur pour cette vente unique
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Description
                </label>
                <textarea
                  name="description"
                  rows="3"
                  defaultValue={selectedProduct?.description || ''}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Image {productFormMode === 'create' && <span className="text-rose-500">*</span>}
                </label>
                <div className={`border-2 border-dashed rounded-lg p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    required={productFormMode === 'create'}
                    className={`hidden w-full ${isDark ? 'text-white' : 'text-gray-800'}`}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDark 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    Choisir un fichier
                  </button>
                  {imagePreview ? (
                    <div className="mt-3">
                      <p className="text-xs text-green-500 mb-2">Nouvelle image</p>
                      <Watermark>
                        <img
                          src={imagePreview}
                          alt="Aperçu"
                          className="max-h-40 rounded-lg mx-auto"
                        />
                      </Watermark>
                    </div>
                  ) : (
                    (() => {
                      const url = resolveProductImageUrl(selectedProduct);
                      return (productFormMode === 'edit' && url) ? (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-2">Image actuelle</p>
                          <Watermark>
                            <img
                              src={url}
                              alt={selectedProduct?.name || 'Image'}
                              className="max-h-40 rounded-lg mx-auto"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </Watermark>
                        </div>
                      ) : null;
                    })()
                  )}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setProductModalOpen(false)}
                  className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                >
                  {productFormMode === 'create' ? 'Créer' : 'Modifier'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {selectedProduct && (
                <>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Nom
                    </label>
                    <p>{selectedProduct.name}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Catégorie
                    </label>
                    <p>{selectedProduct.category}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prix HT
                      </label>
                      <p>{selectedProduct.price_ht}€</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Stock
                      </label>
                      <p>{selectedProduct.stock}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Description
                    </label>
                    <p className={`p-3 rounded-lg ${isDark ? 'bg-[#0b0b0c]' : 'bg-gray-50'}`}>
                      {selectedProduct.description || 'N/A'}
                    </p>
                  </div>
                  {(selectedProduct?.images && selectedProduct.images.length > 0) || selectedProduct?.image ? (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Image
                      </label>
                      <Watermark>
                        <img
                          src={resolveProductImageUrl(selectedProduct)}
                          alt={selectedProduct?.name}
                          className="max-h-40 rounded-lg"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </Watermark>
                    </div>
                  ) : null}
                </>
              )}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <button
                  onClick={() => setProductModalOpen(false)}
                  className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </ModalAdmin>
      </div>
    </div>
  );
};

export default CreatorSpace;
